import type { Audience, Room } from '@lb/domain'

/**
 * The community landing's sections, from the VIEWER's side.
 *
 * The grid used to sit under « Pour les parents · Tous les salons de parents »
 * whatever the audience, so a TSP member read their own rooms as parents'
 * rooms. The headings now follow who is looking:
 *
 *   1. `cohort`  — rooms scoped to a cohort (`cohorts` non-empty). First,
 *      because for a TSP member these are the rooms of the programme they are
 *      in. A cohort-only room has an EMPTY `audiences`, so it must be taken out
 *      before anything reads `audiences`.
 *   2. `segment` — rooms whose `audiences` include the viewer's own segment:
 *      parents' rooms for a parent, pro rooms for a pro, TSP rooms for TSP.
 *   3. `other`   — anything else the server let them see (staff reading every
 *      room, a room shared with a segment that is not theirs). Under a neutral
 *      heading, never under the viewer's segment — a TSP room can therefore
 *      never land under a parents' heading.
 *
 * `audience` undefined (session not resolved yet) puts everything that is not
 * a cohort room in `other` rather than guessing a segment.
 */
export interface RoomGroups {
  cohort: Room[]
  segment: Room[]
  other: Room[]
}

export function groupRooms(
  rooms: readonly Room[],
  audience: Audience | undefined,
  myCohortIds?: readonly string[],
): RoomGroups {
  const groups: RoomGroups = { cohort: [], segment: [], other: [] }
  const segment = audience ? roomSegment(audience) : undefined
  for (const room of rooms) {
    // A member's own cohort first. With `myCohortIds` (a TSP member) only their
    // cohorts count: a room shared with the TSP segment and filed under another
    // cohort is not « your cohort » (review of 25 Sep 2026). Without it (staff,
    // who read every cohort's rooms) any cohort room goes here, as « Cohortes ».
    const mine = myCohortIds
      ? room.cohorts.some((c) => myCohortIds.includes(c.cohortId))
      : room.cohorts.length > 0
    if (mine) groups.cohort.push(room)
    else if (segment && room.audiences.includes(segment)) groups.segment.push(room)
    else groups.other.push(room)
  }
  return groups
}

/**
 * The segment a viewer's rooms are filed under. An org admin reads the
 * practitioner rooms — the server serves them `PRO` (`audienceForRole`) — and
 * no room carries `ORG`, so grouping on `ORG` put an org admin's whole
 * community under « Autres salons » (review of 25 Sep 2026).
 */
export function roomSegment(audience: Audience): Audience {
  return audience === 'ORG' ? 'PRO' : audience
}

/**
 * The i18n suffix for the segment section's heading. ORG reads as the Pro
 * pool: an org admin gets the practitioner shell (see `nav.ts`), and « rooms
 * for organisations » would name a pool no room is filed under.
 */
export function segmentHeadingKeys(audience: Audience): { eyebrow: string; title: string } {
  // Arif's keys (cd9e913), shared with the mobile app so both say the same
  // thing: « Pour les praticiennes », not « professionnel·les » on one app only.
  const segment = roomSegment(audience)
  return { eyebrow: `community.segmentRooms.${segment}.eyebrow`, title: `community.segmentRooms.${segment}.title` }
}

export function segmentCopyKey(audience: Audience): 'parent' | 'pro' | 'tsp' {
  switch (audience) {
    case 'PARENT':
      return 'parent'
    case 'TSP':
      return 'tsp'
    case 'PRO':
    case 'ORG':
      return 'pro'
  }
}

/**
 * The banner naming which portal's community this is, or null for a parent.
 *
 * The prototype draws it in the Pro and TSP shells and not in the parent one
 * (`pro-app.jsx:556`, `tsp-app.jsx:421`), both wrapping the same community
 * view — the sentence is the only thing distinguishing the portals there. It
 * is the design's own answer to « Pros can see the parents' rooms »: the rooms
 * were always scoped, and this says so in words.
 *
 * Routed through `segmentCopyKey`, so the ORG-reads-as-Pro decision lives in
 * one place rather than two.
 */
export function portalBannerKey(audience: Audience | undefined): string | null {
  if (!audience) return null
  const key = segmentCopyKey(audience)
  return key === 'parent' ? null : `community.portalBanner.${key}`
}

/**
 * « Cohorte 2026 · Pod Nord » — the cohort line a cohort room's card wears.
 * One entry per cohort the room is shared with; a whole-cohort entry has no
 * pod. Duplicates collapse, so two pods of one cohort do not print it twice
 * without their pods.
 */
export function cohortLine(room: Pick<Room, 'cohorts'>): string {
  const parts = room.cohorts.map((c) => (c.podName ? `${c.cohortName} · ${c.podName}` : c.cohortName))
  return [...new Set(parts)].join(', ')
}
